import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Slide,
} from '@mui/material';
import {
  ShoppingCart,
  Add,
  Remove,
} from '@mui/icons-material';
import { useMenuStore } from '../../stores/menuStore.js';
import { useOrdersStore } from '../../stores/ordersStore.js';
import { useAuth } from '../../hooks/useAuth.js';
import { OrderService } from '../../services/orderService.js';

/**
 * Mobile Cart Overlay — the current order (cart).
 * Rendered as an inline overlay panel that fills the space between the top bar
 * and the bottom nav + floating button. Not a Drawer.
 */
export const MobileCartDrawer = ({ open, onClose }) => {
  const { outletId, userId } = useAuth();
  const { cart, addToCart, decrementFromCart, clearCart } = useMenuStore();
  const { fetchActiveOrders } = useOrdersStore();
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handlePlaceOrder = async () => {
    if (cart.length === 0) return;
    setIsPlacingOrder(true);
    try {
      await OrderService.createOrder({
        cart_list: cart.map((item) => ({
          Product_ID: item.Product_ID || item.itemID,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        user_id: userId,
        outlet_id: outletId,
        status: 'Active',
      });
      clearCart();
      if (outletId) fetchActiveOrders(outletId);
      onClose();
    } catch (error) {
      console.error('Error placing order:', error);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  if (!open) return null;

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          // Extends from top of screen to just above the nav bar + floating button
          // Nav bar ~56px + safe area, floating button 48px + 8px gap
          bottom: 'calc(112px + env(safe-area-inset-bottom, 0px))',
          zIndex: 1350,
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          bgcolor: 'rgba(255, 255, 255, 0.72)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <ShoppingCart sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            New Order
          </Typography>
        </Box>
        {cart.length > 0 && (
          <Typography
            variant="caption"
            onClick={() => clearCart()}
            sx={{
              color: '#FF3B30',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.75rem',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear All
          </Typography>
        )}
      </Box>

      {/* Cart items */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
        {cart.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
            <ShoppingCart sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 500 }}>
              Tap items to add to cart
            </Typography>
          </Box>
        ) : (
          cart.map((item) => (
            <Box
              key={item.itemID}
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 1.25,
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.88rem' }} noWrap>
                  {item.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.72rem' }}>
                  ₹{item.price} × {item.quantity}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => decrementFromCart(item.itemID)}
                  sx={{ width: 28, height: 28, bgcolor: 'background.default' }}
                >
                  <Remove sx={{ fontSize: 14 }} />
                </IconButton>
                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 22, textAlign: 'center', fontSize: '0.88rem' }}>
                  {item.quantity}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => addToCart(item, 1)}
                  sx={{ width: 28, height: 28, bgcolor: 'background.default' }}
                >
                  <Add sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>

              <Typography
                variant="body2"
                sx={{ fontWeight: 700, ml: 1.5, minWidth: 50, textAlign: 'right', fontSize: '0.88rem' }}
              >
                ₹{(item.price * item.quantity).toFixed(0)}
              </Typography>
            </Box>
          ))
        )}
      </Box>

      {/* Place order footer */}
      {cart.length > 0 && (
        <Box
          sx={{
            p: 2,
            bgcolor: 'rgba(255, 255, 255, 0.72)',
            borderTop: '1px solid rgba(0, 0, 0, 0.06)',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.88rem' }}>
              Total ({cartItemCount} item{cartItemCount !== 1 ? 's' : ''})
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
              ₹{cartTotal.toFixed(2)}
            </Typography>
          </Box>
          <Button
            fullWidth
            variant="contained"
            onClick={handlePlaceOrder}
            disabled={isPlacingOrder}
            startIcon={isPlacingOrder ? <CircularProgress size={16} sx={{ color: 'rgba(255, 255, 255, 0.72)' }} /> : null}
            sx={{
              borderRadius: '14px',
              py: 1.3,
              fontWeight: 700,
              fontSize: '0.92rem',
              bgcolor: 'secondary.main',
              textTransform: 'none',
              '&:hover': { bgcolor: '#D70040' },
              '&.Mui-disabled': { bgcolor: 'rgba(255, 59, 48, 0.15)', color: 'rgba(255, 255, 255, 0.72)' },
            }}
          >
            {isPlacingOrder ? 'Placing...' : 'Place Order'}
          </Button>
        </Box>
      )}
      </Box>
    </Slide>
  );
};
